import { useContractRead, UseContractReadResult } from '@starknet-react/core'
import { Fraction } from '@uniswap/sdk-core'
import { useCallback, useMemo } from 'react'
import { compiledJediswapPair, JEDISWAP_ETH_USDC } from 'src/constants/contracts'
import { Selector } from 'src/constants/misc'
import { decimalsScale } from 'src/utils/decimalScale'
import { BlockNumber, BlockTag, constants, Uint256, uint256 } from 'starknet'

import useChainId from './useChainId'

export function useEtherPrice(blockIdentifier: BlockNumber = BlockTag.latest) {
  const chainId = useChainId()

  const pairReserves = useContractRead({
    abi: compiledJediswapPair, // call is not send if abi is undefined
    address: chainId ? JEDISWAP_ETH_USDC[chainId] : undefined,
    functionName: Selector.GET_RESERVES,
    watch: true,
    blockIdentifier,
  }) as UseContractReadResult & { data?: { reserve0: Uint256; reserve1: Uint256 } }

  return useMemo(() => {
    if (!pairReserves.data) return

    const ethPrice = new Fraction(
      uint256.uint256ToBN(pairReserves.data.reserve1).toString(),
      uint256.uint256ToBN(pairReserves.data.reserve0).toString()
    )

    // token0 and token1 are switched on goerli and mainnet ...
    return (
      chainId === constants.StarknetChainId.SN_GOERLI
        ? new Fraction(ethPrice.denominator, ethPrice.numerator)
        : ethPrice
    ).multiply(decimalsScale(12))
  }, [chainId, pairReserves.data])
}

export function useWeiAmountToParsedFiatValue(): (amount?: Fraction) => string | null {
  const etherPrice = useEtherPrice()

  return useCallback(
    (amount?: Fraction) =>
      etherPrice && amount
        ? `$${(Math.round(+amount.multiply(etherPrice).toFixed(6) * 100) / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : null,
    [etherPrice]
  )
}
